import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthTokenStorageService } from './auth-token-storage.service';
import { RefreshAuthToken } from '../model/tokens/refresh-auth-token';
import { AuthToken } from '../model/tokens/auth-token';
import { HttpAuthService } from './http-auth.service';
import { HttpEvent, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Tokens } from '../model/tokens/tokens';

@Injectable({
  providedIn: 'root',
})
export class JwtWrapperService {
  private jwtHelperService: JwtHelperService = new JwtHelperService(); // Non injectable with DI
  constructor(
    private authTokenStorageService: AuthTokenStorageService,
    private authService: HttpAuthService,
  ) {}

  private setupTokens(value: HttpEvent<Tokens>): boolean {
    if (value instanceof HttpResponse) {
      const tokens: Tokens | null = value.body;

      // récupération du jeton jwt
      if (tokens !== null && tokens.token.token && tokens.refresh_token.token) {
        // construction du token
        const localAuthToken: AuthToken = tokens.token;
        const localRefreshAuthToken: RefreshAuthToken = tokens.refresh_token;

        // On persiste les nouveaux tokens
        this.authTokenStorageService.setToken(localAuthToken);
        this.authTokenStorageService.setRefreshToken(localRefreshAuthToken);

        return true;
      }
    }
    return false;
  }
  /**
   * Récupère le token jwt à partir du refresh token
   */
  public loginWithRefreshToken(): Observable<boolean> {
    return this.authService.refreshToken().pipe(
      map((value: HttpEvent<Tokens>) => {
        return this.setupTokens(value);
      }),
    );
  }

  public isLoggedIn(): boolean {
    try {
      const authToken: AuthToken = this.getAuthToken();
      return !this.jwtHelperService.isTokenExpired(authToken.token);
    } catch (error) {
      return false;
    }
  }

  public isTokenExpired(): boolean {
    try {
      const authToken: AuthToken = this.getAuthToken();
      return !!this.jwtHelperService.isTokenExpired(authToken.token);
    } catch (error) {
      return false;
    }
  }

  public tokenExist(): boolean {
    try {
      this.getAuthToken();
      return true;
    } catch (error) {
      return false;
    }
  }

  public getExpiration(): Date {
    const authToken: AuthToken = this.getAuthToken();
    const dateExpiration: Date | null = this.jwtHelperService.getTokenExpirationDate(authToken.token);
    if (dateExpiration) {
      return dateExpiration;
    } else {
      throw new Error("Aucune date d'expiration présente dans le token");
    }
  }

  private getAuthToken(): AuthToken {
    const authToken: AuthToken | null = this.authTokenStorageService.getToken();
    if (authToken) {
      return authToken;
    } else {
      throw new Error('Aucun token présent');
    }
  }

  /* Refresh Token */
  public refreshTokenExist(): boolean {
    try {
      this.getRefreshToken();
      return true;
    } catch (error) {
      return false;
    }
  }

  private getRefreshToken(): RefreshAuthToken {
    const refreshAuthToken: RefreshAuthToken | null = this.authTokenStorageService.getRefreshToken();
    if (refreshAuthToken) {
      return refreshAuthToken;
    } else {
      throw new Error('Aucun refresh token présent');
    }
  }

  public isRefreshTokenExpired(): boolean {
    try {
      const refreshAuthToken: RefreshAuthToken = this.getRefreshToken();
      return !!this.jwtHelperService.isTokenExpired(refreshAuthToken.token);
    } catch (error) {
      return false;
    }
  }
}
